import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { formatSafe } from "@/lib/dateUtils";
import {
  CheckCircle, AlertTriangle, CalendarDays, DollarSign,
  Package, FileText, Clock, ArrowRight, CalendarIcon,
  ShieldCheck, XCircle, RotateCcw,
} from "lucide-react";
import type { Opportunity } from "@/data/opportunityTypes";
import { calculatePrice } from "@/data/pricingEngine";
import { useProductOptions } from "@/data/ProductOptionsContext";
import { OBJECTION_LABELS, type ObjectionCode } from "@/data/appointmentOutcomeTypes";
import { Input } from "@/components/ui/input";

// ── Types ──

type BuildOutcome = "sold" | "no_sale" | "rescheduled";
type NoBuildOutcome = "no_demo";

const NO_SALE_REASONS = [
  { value: "price_too_high", label: "Price too high" },
  { value: "not_ready", label: "Not ready" },
  { value: "shopping_competitors", label: "Shopping competitors" },
  { value: "financing_declined", label: "Financing declined" },
  { value: "scope_mismatch", label: "Scope mismatch" },
  { value: "other", label: "Other" },
] as const;

const NO_DEMO_REASONS = [
  { value: "customer_not_home", label: "Customer not home" },
  { value: "porched", label: "Porched" },
  { value: "could_not_access", label: "Could not access" },
  { value: "rescheduled", label: "Rescheduled" },
  { value: "other", label: "Other" },
] as const;

export interface ResultAppointmentData {
  hasBuild: boolean;
  outcome: BuildOutcome | NoBuildOutcome;
  reason?: string;
  notes: string;
  rescheduleDate?: string;
  rescheduleTime?: string;
  paymentMethodConfirmed?: string;
  // Pricing snapshot (immutable, read-only)
  pricingSnapshot?: {
    standardPrice: number;
    promoPrice: number;
    bestValuePrice: number;
    paymentMethod: string;
    productSummary: string;
  };
  // ── Structured Outcome (Phase 1 extension) ──
  // Captured for sold + no_sale outcomes so the AppointmentOutcomeExtension
  // sidecar can be written immutably alongside the AppointmentResult.
  proposalCapture?: {
    packageTier: string;          // "Premium" | "Standard" | custom
    discountAmount: number;       // 0 if none
    discountReason?: string;
    financingTerms?: string;
  };
  objectionsCapture?: {
    primary: ObjectionCode;
    secondary?: ObjectionCode;
    notes?: string;
  };
}

interface Props {
  lead: Opportunity;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ResultAppointmentData) => void;
  onValidateReschedule?: (date: string, time: string) => string | null;
}

export function ResultAppointmentDialog({ lead, open, onClose, onSubmit, onValidateReschedule }: Props) {
  const { options: allOptions } = useProductOptions();

  // ── Auto-detect build state ──
  const hasBuild = !!(lead.projectConfig && lead.projectConfig.bathroomSystemType);

  // ── Derive pricing from build (read-only) ──
  const pricing = useMemo(() => {
    if (!hasBuild || !lead.projectConfig) return null;
    const breakdown = calculatePrice(lead.projectConfig, allOptions);
    const standard = breakdown.finalTotal;
    const promo = Math.round(standard * 0.83);
    const bestValue = Math.round(promo * 0.88);
    return { standard, promo, bestValue, breakdown };
  }, [hasBuild, lead.projectConfig, allOptions]);

  // ── Determine product summary ──
  const productSummary = useMemo(() => {
    if (!lead.projectConfig) return lead.projectType || "Unknown";
    const material = lead.projectConfig.bathroomMaterials?.[0] || lead.projectConfig.primaryMaterial || "";
    const system = lead.projectConfig.bathroomSystemType || "";
    return [material, system, lead.projectType].filter(Boolean).join(" · ");
  }, [lead]);

  // ── State ──
  const [outcome, setOutcome] = useState<BuildOutcome | NoBuildOutcome | null>(hasBuild ? null : null);
  const [reason, setReason] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [rescheduleDate, setRescheduleDate] = useState<Date | undefined>();
  const [rescheduleTime, setRescheduleTime] = useState("09:00");
  const [paymentConfirmed, setPaymentConfirmed] = useState<string>("");

  // ── Structured Outcome (Phase 1 sidecar) ──
  const [packageTier, setPackageTier] = useState<string>("Standard");
  const [discountAmount, setDiscountAmount] = useState<string>("0");
  const [discountReason, setDiscountReason] = useState<string>("");
  const [financingTerms, setFinancingTerms] = useState<string>("");
  const [primaryObjection, setPrimaryObjection] = useState<ObjectionCode | "">("");
  const [secondaryObjection, setSecondaryObjection] = useState<ObjectionCode | "">("");
  const [objectionNotes, setObjectionNotes] = useState<string>("");

  // ── Validation ──
  const isValid = useMemo(() => {
    if (!outcome) return false;
    if (outcome === "no_sale" && !reason) return false;
    if (outcome === "no_demo" && !reason) return false;
    if (outcome === "sold" && hasBuild && !paymentConfirmed) return false;
    if ((outcome === "rescheduled" || (outcome === "no_demo" && reason === "rescheduled")) && !rescheduleDate) return false;
    if (!notes.trim()) return false;
    // Structured outcome: no_sale must capture primary objection.
    if (outcome === "no_sale" && !primaryObjection) return false;
    if (outcome === "no_sale" && primaryObjection === "other" && !objectionNotes.trim()) return false;
    return true;
  }, [outcome, reason, notes, rescheduleDate, paymentConfirmed, hasBuild, primaryObjection, objectionNotes]);

  const captureProposal = (outcome === "sold" || outcome === "no_sale") && hasBuild;
  const captureObjections = outcome === "no_sale";

  const handleSubmit = () => {
    if (!isValid || !outcome) return;
    const discountNum = Number(discountAmount) || 0;
    onSubmit({
      hasBuild,
      outcome,
      reason: reason || undefined,
      notes,
      rescheduleDate: rescheduleDate ? formatSafe(rescheduleDate, "yyyy-MM-dd") : undefined,
      rescheduleTime: rescheduleDate ? rescheduleTime : undefined,
      paymentMethodConfirmed: paymentConfirmed || undefined,
      pricingSnapshot: pricing ? {
        standardPrice: pricing.standard,
        promoPrice: pricing.promo,
        bestValuePrice: pricing.bestValue,
        paymentMethod: paymentConfirmed || "N/A",
        productSummary,
      } : undefined,
      proposalCapture: captureProposal ? {
        packageTier,
        discountAmount: discountNum,
        discountReason: discountReason.trim() || undefined,
        financingTerms: (paymentConfirmed === "financing" && financingTerms.trim())
          ? financingTerms.trim()
          : undefined,
      } : undefined,
      objectionsCapture: captureObjections && primaryObjection ? {
        primary: primaryObjection,
        secondary: secondaryObjection || undefined,
        notes: objectionNotes.trim() || undefined,
      } : undefined,
    });
  };

  const needsReschedule = outcome === "rescheduled" || (outcome === "no_demo" && reason === "rescheduled");
  const rescheduleConflictMessage = useMemo(() => {
    if (!needsReschedule || !rescheduleDate || !onValidateReschedule) return null;
    return onValidateReschedule(formatSafe(rescheduleDate, "yyyy-MM-dd"), rescheduleTime);
  }, [needsReschedule, rescheduleDate, rescheduleTime, onValidateReschedule]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) return; /* prevent escape */ }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto [&>button]:hidden" onPointerDownOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader className="pb-0">
          <DialogTitle className="text-base font-bold flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Result Appointment
          </DialogTitle>
          <DialogDescription className="text-xs">
            {lead.primaryName} · {lead.address}, {lead.city}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* ── BUILD STATUS INDICATOR ── */}
          <div className={cn(
            "flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold border",
            hasBuild
              ? "bg-success/10 border-success/20 text-success"
              : "bg-warning/10 border-warning/20 text-warning"
          )}>
            {hasBuild ? <Package className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            {hasBuild ? "Build Completed — Pricing Available" : "No Build Data — Auto-classified as No Demo"}
          </div>

          {/* ── BUILD SUMMARY (read-only) ── */}
          {hasBuild && pricing && (
            <Card className="border-border/50 shadow-none">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Build Summary</p>
                  <Badge variant="outline" className="text-[9px] h-4 gap-1">
                    <ShieldCheck className="h-2.5 w-2.5" /> Read-Only
                  </Badge>
                </div>

                <p className="text-xs text-foreground font-medium">{productSummary}</p>

                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 rounded-md bg-muted/30 border border-border/30">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">1 Year Price</p>
                    <p className="text-sm font-bold text-foreground mt-0.5">${pricing.standard.toLocaleString()}</p>
                  </div>
                  <div className="text-center p-2 rounded-md bg-primary/5 border border-primary/20">
                    <p className="text-[9px] text-primary uppercase tracking-wider font-semibold">Promo Price</p>
                    <p className="text-sm font-bold text-primary mt-0.5">${pricing.promo.toLocaleString()}</p>
                  </div>
                  <div className="text-center p-2 rounded-md bg-success/5 border border-success/20">
                    <p className="text-[9px] text-success uppercase tracking-wider font-semibold">Best Value</p>
                    <p className="text-sm font-bold text-success mt-0.5">${pricing.bestValue.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Separator />

          {/* ── OUTCOME SELECTION ── */}
          <div className="space-y-2">
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Outcome</p>
            {hasBuild ? (
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: "sold" as const, label: "Sold", icon: CheckCircle, color: "text-success border-success/30 bg-success/5" },
                  { value: "no_sale" as const, label: "No Sale", icon: XCircle, color: "text-destructive border-destructive/30 bg-destructive/5" },
                  { value: "rescheduled" as const, label: "Rescheduled", icon: RotateCcw, color: "text-warning border-warning/30 bg-warning/5" },
                ]).map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => { setOutcome(opt.value); setReason(""); }}
                    className={cn(
                      "flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all text-xs font-semibold",
                      outcome === opt.value
                        ? `${opt.color} ring-2 ring-offset-1 ring-primary/30`
                        : "border-border/50 text-muted-foreground hover:border-border"
                    )}
                  >
                    <opt.icon className="h-5 w-5" />
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <button
                  onClick={() => setOutcome("no_demo")}
                  className={cn(
                    "w-full flex items-center gap-2 p-3 rounded-lg border-2 transition-all text-xs font-semibold",
                    outcome === "no_demo"
                      ? "text-warning border-warning/30 bg-warning/5 ring-2 ring-offset-1 ring-primary/30"
                      : "border-border/50 text-muted-foreground hover:border-border"
                  )}
                >
                  <AlertTriangle className="h-4 w-4" />
                  No Demo
                </button>
              </div>
            )}
          </div>

          {/* ── REASON (conditional) ── */}
          {(outcome === "no_sale" || outcome === "no_demo") && (
            <div className="space-y-2">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Reason *</p>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  {(outcome === "no_sale" ? NO_SALE_REASONS : NO_DEMO_REASONS).map(r => (
                    <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* ── SOLD: Confirm payment method ── */}
          {outcome === "sold" && hasBuild && (
            <div className="space-y-2">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Confirm Payment Method *</p>
              <Select value={paymentConfirmed} onValueChange={setPaymentConfirmed}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="Select payment method..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash" className="text-xs">Cash / Check</SelectItem>
                  <SelectItem value="financing" className="text-xs">Financing</SelectItem>
                  <SelectItem value="credit_card" className="text-xs">Credit Card</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* ── RESCHEDULE: Date picker ── */}
          {needsReschedule && (
            <div className="space-y-2">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Reschedule Date *</p>
              <div className="flex gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("flex-1 h-9 justify-start text-left text-xs font-normal", !rescheduleDate && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                      {rescheduleDate ? formatSafe(rescheduleDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={rescheduleDate}
                      onSelect={setRescheduleDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
                <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                  <SelectTrigger className="w-28 h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00"].map(t => (
                      <SelectItem key={t} value={t} className="text-xs">
                        {formatSafe(new Date(`2000-01-01T${t}`), "h:mm a")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {rescheduleConflictMessage && (
                <div className="rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs text-warning">
                  {rescheduleConflictMessage}
                </div>
              )}
            </div>
          )}

          {/* ── PROPOSAL SNAPSHOT (sold + no_sale w/ build) ── */}
          {captureProposal && (
            <div className="space-y-2 rounded-lg border border-border/50 p-3 bg-muted/20">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Proposal Snapshot</p>
                <Badge variant="outline" className="text-[9px] h-4 gap-1">
                  <ShieldCheck className="h-2.5 w-2.5" /> Immutable
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-muted-foreground">Package presented</label>
                  <Select value={packageTier} onValueChange={setPackageTier}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Standard" className="text-xs">Standard</SelectItem>
                      <SelectItem value="Premium" className="text-xs">Premium</SelectItem>
                      <SelectItem value="Best Value" className="text-xs">Best Value</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-muted-foreground">Discount applied ($)</label>
                  <Input
                    type="number"
                    inputMode="decimal"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    className="h-8 text-xs"
                    min={0}
                  />
                </div>
              </div>
              {Number(discountAmount) > 0 && (
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-muted-foreground">Discount reason</label>
                  <Input
                    value={discountReason}
                    onChange={(e) => setDiscountReason(e.target.value)}
                    placeholder="e.g. Same-day close, Manager promo"
                    className="h-8 text-xs"
                  />
                </div>
              )}
              {paymentConfirmed === "financing" && (
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-muted-foreground">Financing terms</label>
                  <Input
                    value={financingTerms}
                    onChange={(e) => setFinancingTerms(e.target.value)}
                    placeholder="e.g. 120 mo @ 9.99% (GreenSky)"
                    className="h-8 text-xs"
                  />
                </div>
              )}
            </div>
          )}

          {/* ── STRUCTURED OBJECTIONS (no_sale only) ── */}
          {captureObjections && (
            <div className="space-y-2 rounded-lg border border-warning/30 p-3 bg-warning/5">
              <p className="text-[11px] font-bold text-warning uppercase tracking-wider">Structured Objections</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-muted-foreground">Primary blocker *</label>
                  <Select value={primaryObjection} onValueChange={(v) => setPrimaryObjection(v as ObjectionCode)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {(Object.keys(OBJECTION_LABELS) as ObjectionCode[]).map(code => (
                        <SelectItem key={code} value={code} className="text-xs">{OBJECTION_LABELS[code]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-muted-foreground">Secondary (optional)</label>
                  <Select value={secondaryObjection} onValueChange={(v) => setSecondaryObjection(v as ObjectionCode)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      {(Object.keys(OBJECTION_LABELS) as ObjectionCode[])
                        .filter(c => c !== primaryObjection)
                        .map(code => (
                          <SelectItem key={code} value={code} className="text-xs">{OBJECTION_LABELS[code]}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {primaryObjection === "other" && (
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-muted-foreground">Describe blocker *</label>
                  <Input
                    value={objectionNotes}
                    onChange={(e) => setObjectionNotes(e.target.value)}
                    placeholder="Required when primary is Other"
                    className="h-8 text-xs"
                  />
                </div>
              )}
              {primaryObjection && primaryObjection !== "other" && (
                <div className="space-y-1">
                  <label className="text-[10px] font-semibold text-muted-foreground">Objection notes (optional)</label>
                  <Input
                    value={objectionNotes}
                    onChange={(e) => setObjectionNotes(e.target.value)}
                    placeholder="Specifics — e.g. 'Wife travels Mon-Wed, re-engage Thursday'"
                    className="h-8 text-xs"
                  />
                </div>
              )}
            </div>
          )}


          <div className="space-y-2">
            <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">Notes *</p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Document the appointment result..."
              className="min-h-[70px] text-xs bg-card"
            />
          </div>

          <Separator />

          {/* ── ACTIONS ── */}
          <div className="flex items-center gap-2">
            <Button
              className="flex-1 h-10 text-xs font-bold gap-1.5"
              disabled={!isValid || !!rescheduleConflictMessage}
              onClick={handleSubmit}
            >
              <ArrowRight className="h-4 w-4" />
              Submit Result
            </Button>
          </div>

          <p className="text-[9px] text-muted-foreground text-center">
            This result is permanent and cannot be edited after submission.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
